import recommend from '@algolia/recommend';
import {
  ALGOLIA_APP_ID,
  ALGOLIA_SEARCH_API_KEY,
  searchClient,
  PRODUCT_INDEX,
} from './algoliaClient';
import {
  getAffinities,
  getCategoryRoot,
  getEffectiveProductPrice,
  extractProductAttrs,
  grapeMatchesAffinity,
  pickGrapeReason,
  grapesMatch,
  normalizeGrapeKey,
  parseGrapeEntries,
  tokenizeGrapes,
} from '../utils/userProfile';

const recommendClient = recommend(ALGOLIA_APP_ID, ALGOLIA_SEARCH_API_KEY);

export const UPSELL_CONFIG = {
  minUpPct: 0.1,
  maxUpPct: 0.4,
  candidatePoolSize: 20,
  maxHistoryEvents: 50,
  autoDismissMs: 15000,
};

function escapeFilterValue(s) {
  return String(s).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

function buildNotObjectIdFilters(ids) {
  const unique = [...new Set((ids || []).filter(Boolean).map(String))];
  return unique.slice(0, 24).map((id) => `NOT objectID:${escapeFilterValue(id)}`).join(' AND ');
}

function sameCategoryRoot(origRoot, hit) {
  if (!origRoot) return true;
  const hitRoot = getCategoryRoot(hit.categorias_flat);
  return hitRoot === origRoot;
}

const WINE_COLOR_FAMILIES = new Set(['red', 'white', 'rose', 'orange']);

function normalizeColorText(text) {
  return String(text || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '');
}

/** Normalize wine color to a comparable family (red, white, rose, …). */
export function wineColorFamily(color) {
  const s = normalizeColorText(color);
  if (!s) return '';

  // Rosé first — never use loose substring checks (e.g. "prosecco" contains "rose")
  if (s === 'rose' || s.startsWith('rose ') || /^vin ros[eé]/.test(s) || /\bros[eé]\b/.test(s)) {
    return 'rose';
  }
  if (s === 'rouge' || s === 'red' || s.startsWith('rouge ') || s.startsWith('red ') || /\brouge\b/.test(s)) {
    return 'red';
  }
  if (s === 'blanc' || s === 'white' || s.startsWith('blanc ') || s.startsWith('white ') || /\bblanc\b/.test(s)) {
    return 'white';
  }
  if (/\borange\b/.test(s)) return 'orange';
  return '';
}

/** Infer wine color from a category path or label. */
function wineColorFromCategoryText(text) {
  const s = normalizeColorText(text);
  if (!s) return '';

  if (/\bvin ros[eé]\b|\bros[eé]\b/.test(s)) return 'rose';
  if (/\bvin rouge\b|\brouge\b/.test(s)) return 'red';
  if (/\bvin blanc\b|\bblanc\b/.test(s)) return 'white';
  if (/\borange\b/.test(s)) return 'orange';
  return '';
}

function categoriasPaths(product) {
  const cf = product?.categorias_flat;
  if (Array.isArray(cf)) return cf.filter(Boolean).map(String);
  if (cf) return [String(cf)];
  return [];
}

/** Resolve color from product.color or all categorias_flat paths. */
export function getProductColorFamily(product) {
  if (!product) return '';

  const fromColor = wineColorFamily(product.color);
  if (fromColor) return fromColor;

  for (const path of categoriasPaths(product)) {
    const family = wineColorFromCategoryText(path);
    if (family) return family;
  }

  const combined = categoriasPaths(product).join(' ');
  return wineColorFromCategoryText(combined);
}

function isWineProduct(product) {
  const root = getCategoryRoot(product?.categorias_flat);
  return root === 'Vin' || root === 'Wine' || root === 'Vino';
}

function sameWineColorFamily(origProduct, candidate) {
  const origFamily = getProductColorFamily(origProduct);
  const candFamily = getProductColorFamily(candidate);

  if (isWineProduct(origProduct)) {
    if (!origFamily || !candFamily) return false;
    if (origFamily !== candFamily) return false;
    // Red ↔ rosé and white ↔ rosé are never valid upgrades
    if (origFamily === 'red' && candFamily === 'rose') return false;
    if (origFamily === 'white' && candFamily === 'rose') return false;
    if (origFamily === 'rose' && (candFamily === 'red' || candFamily === 'white')) return false;
    return WINE_COLOR_FAMILIES.has(origFamily);
  }

  if (!origFamily) return true;
  if (!candFamily) return false;
  return origFamily === candFamily;
}

function hasAnyGrapeOverlap(origCepage, candCepage) {
  const origKeys = tokenizeGrapes(origCepage).map(normalizeGrapeKey);
  if (!origKeys.length) return false;
  for (const entry of parseGrapeEntries(candCepage)) {
    for (const o of origKeys) {
      if (grapesMatch(entry.normalized, o)) return true;
    }
  }
  return false;
}

function scoreCandidate(original, candidate, affinities, origPrice, candPrice, origAttrs) {
  let score = 0;
  const reasons = [];
  const candAttrs = extractProductAttrs(candidate);

  const origColorFamily = getProductColorFamily(original);
  const candColorFamily = getProductColorFamily(candidate);
  if (origColorFamily && candColorFamily === origColorFamily) {
    score += 5;
    reasons.push({ key: 'color', value: candAttrs.color || origAttrs.color });
  }

  const grapeLabel = pickGrapeReason({
    origCepage: origAttrs.cepage,
    candCepage: candAttrs.cepage,
    topGrapes: affinities.topGrapes,
  });
  if (grapeLabel) {
    if (hasAnyGrapeOverlap(origAttrs.cepage, candAttrs.cepage)) {
      score += 3;
    } else if (grapeMatchesAffinity(candAttrs.cepage, affinities.topGrapes)) {
      score += 2;
    }
    reasons.push({ key: 'grape', value: grapeLabel });
  }

  if (
    origAttrs.region_origine &&
    candAttrs.region_origine &&
    origAttrs.region_origine === candAttrs.region_origine
  ) {
    score += 2;
    reasons.push({ key: 'region', value: candAttrs.region_origine });
  } else {
    const topR = affinities.topRegions.slice(0, 3);
    if (candAttrs.region_origine && topR.includes(candAttrs.region_origine)) {
      score += 1;
      reasons.push({ key: 'region', value: candAttrs.region_origine });
    }
  }

  if (
    origAttrs.pays_origine &&
    candAttrs.pays_origine &&
    origAttrs.pays_origine === candAttrs.pays_origine
  ) {
    score += 2;
    reasons.push({ key: 'country', value: candAttrs.pays_origine });
  } else {
    const topC = affinities.topCountries.slice(0, 3);
    if (candAttrs.pays_origine && topC.includes(candAttrs.pays_origine)) {
      score += 3;
      reasons.push({ key: 'country', value: candAttrs.pays_origine });
    }
  }

  const { p25, p75 } = affinities.priceBand;
  if (p25 != null && p75 != null && candPrice >= p25 && candPrice <= p75) {
    score += 1;
    reasons.push({ key: 'priceBand', value: true });
  }

  const origRating = origAttrs.rating;
  const candRating = candAttrs.rating;
  if (origRating != null && candRating != null && candRating > origRating) {
    score += 1;
    reasons.push({ key: 'betterRating', value: candRating });
  }

  const delta = candPrice - origPrice;
  const deltaPct = origPrice > 0 ? delta / origPrice : 0;

  return { score, reasons, candAttrs, delta, deltaPct };
}

function dedupeHits(hitsArrays) {
  const seen = new Set();
  const out = [];
  for (const arr of hitsArrays) {
    for (const h of arr || []) {
      if (!h?.objectID || seen.has(h.objectID)) continue;
      seen.add(h.objectID);
      out.push(h);
    }
  }
  return out;
}

/**
 * @param {object} product - Product just added (includes selectedStore)
 * @param {{ excludeObjectIds?: string[], minUpPct?: number, maxUpPct?: number }} opts
 * @returns {Promise<{ product: object, reasons: object[], priceDelta: number, priceDeltaPct: number } | null>}
 */
export async function findAlternative(product, opts = {}) {
  const excludeObjectIds = opts.excludeObjectIds || [];
  const minUp = opts.minUpPct ?? UPSELL_CONFIG.minUpPct;
  const maxUp = opts.maxUpPct ?? UPSELL_CONFIG.maxUpPct;
  const maxRec = opts.candidatePoolSize ?? UPSELL_CONFIG.candidatePoolSize;

  const origId = String(product.objectID);
  const origPrice = getEffectiveProductPrice(product);
  if (origPrice <= 0) return null;

  const priceMin = origPrice * (1 + minUp);
  const priceMax = origPrice * (1 + maxUp);
  const origRoot = getCategoryRoot(product.categorias_flat);
  const origAttrs = extractProductAttrs(product);
  const affinities = getAffinities();

  const baseRequest = {
    indexName: PRODUCT_INDEX,
    objectID: origId,
    threshold: 0,
    maxRecommendations: maxRec,
    queryParameters: {},
  };

  let relatedHits = [];
  let similarHits = [];
  try {
    const [relatedRes, similarRes] = await Promise.all([
      recommendClient.getRelatedProducts([baseRequest]),
      recommendClient.getLookingSimilar([baseRequest]),
    ]);
    relatedHits = relatedRes?.results?.[0]?.hits || [];
    similarHits = similarRes?.results?.[0]?.hits || [];
  } catch (e) {
    console.warn('[alternativeFinder] Recommend API error', e);
  }

  const pool = dedupeHits([relatedHits, similarHits]).filter((h) => h.objectID !== origId);
  const excludeSet = new Set(excludeObjectIds.map(String));

  let filtered = pool.filter((h) => {
    if (excludeSet.has(String(h.objectID))) return false;
    const p = getEffectiveProductPrice({ ...h, selectedStore: product.selectedStore });
    if (p < priceMin || p > priceMax) return false;
    if (!sameCategoryRoot(origRoot, h)) return false;
    if (!sameWineColorFamily(product, h)) return false;
    return true;
  });

  if (filtered.length === 0) {
    const notFilters = buildNotObjectIdFilters([origId, ...excludeObjectIds]);
    const origColorLabel = origAttrs.color || '';
    const origFamily = getProductColorFamily(product);
    const preferCountry = origAttrs.pays_origine || affinities.topCountries[0];
    const filtersParts = [
      `price:${priceMin.toFixed(2)} TO ${priceMax.toFixed(2)}`,
    ];
    if (notFilters) filtersParts.push(notFilters);
    if (origFamily === 'red' || origFamily === 'white') {
      filtersParts.push('NOT color:Rosé');
      filtersParts.push('NOT color:Rose');
    }
    const params = {
      filters: filtersParts.join(' AND '),
      hitsPerPage: 24,
    };
    const facetFilters = [];
    if (origColorLabel) {
      facetFilters.push([`color:${origColorLabel}`]);
    }
    if (preferCountry) {
      facetFilters.push([`attributes.pays_origine:${preferCountry}`]);
    }
    if (facetFilters.length) params.facetFilters = facetFilters;
    try {
      const res = await searchClient.search([
        {
          indexName: PRODUCT_INDEX,
          params,
        },
      ]);
      filtered = res?.results?.[0]?.hits || [];
      filtered = filtered.filter((h) => {
        if (excludeSet.has(String(h.objectID))) return false;
        const p = getEffectiveProductPrice({ ...h, selectedStore: product.selectedStore });
        return (
          p >= priceMin &&
          p <= priceMax &&
          sameCategoryRoot(origRoot, h) &&
          sameWineColorFamily(product, h)
        );
      });
    } catch (e) {
      console.warn('[alternativeFinder] Fallback search error', e);
      return null;
    }
  }

  if (filtered.length === 0) return null;

  const scored = filtered
    .map((hit) => {
      const candPrice = getEffectiveProductPrice({ ...hit, selectedStore: product.selectedStore });
      const { score, reasons, delta, deltaPct } = scoreCandidate(
        product,
        hit,
        affinities,
        origPrice,
        candPrice,
        origAttrs
      );
      const candRating = extractProductAttrs(hit).rating ?? 0;
      return { hit, score, reasons, delta, deltaPct, candPrice, candRating };
    })
    .sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      if (a.delta !== b.delta) return a.delta - b.delta;
      return b.candRating - a.candRating;
    });

  const best = scored[0];
  if (!best) return null;

  return {
    product: best.hit,
    reasons: best.reasons,
    priceDelta: best.delta,
    priceDeltaPct: best.deltaPct,
  };
}
